import classNames from 'classnames';
import React, { useMemo, useEffect, useRef } from 'react';
import { BinProfileIngredient } from '~/bins/bin-level/bin-profile-ingredient';
import { makeStyles, List, ListItem, ListItemText, ListItemButton, Typography, Paper } from '~/lib/bn-material-ui';
import { useBinLevelMeasurementOverrides, useMeasurement } from '~/utility/BinSentry-ui-utility';
import { BARN_ASSUMED_BULK_DENSITY } from '~/organizations/barns/barn-page/insights-graph/barn-insights-helpers';
import { useLatestLevelFromPageNow } from '../bin-props';

const useStyles = makeStyles(theme => ({
  container: {
    width: '15rem',
    height: '100%',
    backgroundColor: theme.palette.gray[10],
    borderRadius: 0,
    borderBottom: 'none',
  },
  noRightBorder: {
    borderRightWidth: 0,
  },
  list: {
    padding: 0,
    height: '100%',
    overflow: 'auto',
  },
  header: {
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  listItem: {
    display: 'flex',
    gap: '1rem',
    borderTop: '1px solid transparent',
    borderBottom: '4px solid transparent',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.gray[20],
    },
  },
  levelRow: {
    display: 'flex',
    gap: '0.4rem',
    '& span': {
      fontWeight: 'bold',
    },
  },
  selectedItem: {
    backgroundColor: theme.palette.white,
    borderTopColor: theme.palette.gray[40],
    borderBottomColor: theme.palette.blue[20],
    '&:hover': {
      backgroundColor: theme.palette.gray[40],
    },
  },
  selectedBinName: {
    color: theme.palette.blue[60],
  },
  binName: {
    fontWeight: 600,
  },
  binProfileContainer: {
    width: '1.5rem',
    height: '6rem',
  },
  binProfile: {
    height: '100%',
    flex: '0 0 2.5rem',
  },
  binDetails: {
    fontSize: '0.875rem',
    color: theme.palette.gray[60],
    '& *': {
      width: '100%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
  },
}));

/**
 * Bin list component that displays a list of bins with clickable items
 * @param {Object} props - Component props
 * @param {Array} props.bins - Array of bin entities
 * @param {string} props.selectedBinId - ID of the currently selected bin
 * @param {Function} props.onBinSelect - Callback when a bin is selected
 * @param {string} props.sort - Sort field ('name', 'volume', 'percent', 'material')
 * @param {string} props.order - Sort order ('asc' or 'desc')
 * @returns {JSX.Element} Bin list component
 */
export default function InventoryMapBinList({
  bins = [],
  selectedBinId,
  onBinSelect,
  sort = 'name',
  order = 'asc',
}) {
  const styles = useStyles();
  const listRef = useRef(null);

  const handleBinClick = (bin) => {
    onBinSelect(bin);
  };

  // Sort bins to put filtered ones at the bottom, with secondary sorting within each group
  const sortedBins = useMemo(() => {
    return [...bins].sort((a, b) => {
      // Primary sort: filtered bins go to bottom
      if (a.isFiltered && !b.isFiltered) {
        return 1;
      }
      if (!a.isFiltered && b.isFiltered) {
        return -1;
      }

      // Secondary sort: within each group (filtered/non-filtered), sort by the specified field
      let aValue, bValue;

      switch (sort) {
        case 'name':
          aValue = a.entity?.properties?.name || '';
          bValue = b.entity?.properties?.name || '';
          break;
        case 'volume':
          aValue = a.latestVolume || 0;
          bValue = b.latestVolume || 0;
          break;
        case 'percent':
          aValue = a.latestVolume && a.fullTotal ? (a.latestVolume / a.fullTotal) * 100 : 0;
          bValue = b.latestVolume && b.fullTotal ? (b.latestVolume / b.fullTotal) * 100 : 0;
          break;
        case 'material':
          aValue = a.material || '';
          bValue = b.material || '';
          break;
        default:
          aValue = a.entity?.properties?.name || '';
          bValue = b.entity?.properties?.name || '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return order === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparison
      const comparison = aValue - bValue;
      return order === 'asc' ? comparison : -comparison;
    });
  }, [bins, sort, order]);

  // Auto-scroll the selected bin into view when selection changes
  useEffect(() => {
    if (!selectedBinId || !listRef.current) {
      return;
    }

    const safeId = String(selectedBinId).replace(/"/g, '\\"');
    const el = listRef.current.querySelector(`[data-bin-id="${safeId}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedBinId, sortedBins]);

  return (
    <Paper className={classNames(styles.container, !selectedBinId ? styles.noRightBorder : undefined )} variant='outlined'>
      <List className={styles.list} ref={listRef}>
        <div className={styles.header} key='header'>
          <Typography variant='h6'>Bins ({bins.length})</Typography>
        </div>
        {sortedBins.map((bin) => {
          const binEntity = bin.entity;
          const binId = binEntity.getLink('self').href;
          const isSelected = selectedBinId === binId;
          const isFiltered = !isSelected && bin.isFiltered;

          return (
            <ListItem key={binId} disablePadding>
              <ListItemButton
                className={`${styles.listItem} ${isSelected ? styles.selectedItem : ''}`}
                onClick={() => handleBinClick(bin)}
                data-bin-id={binId}
                style={{ opacity: isFiltered ? 0.3 : 1 }}
              >
                <div className={styles.binProfileContainer}>
                  <BinProfileIngredient href={binId} className={styles.binProfile}/>
                </div>

                <ListItemText
                  primary={
                    <Typography className={classNames(styles.binName, isSelected && styles.selectedBinName)}>
                      {binEntity.properties.name}
                    </Typography>
                  }
                  secondary={
                    <div className={styles.binDetails}>
                      <div className={styles.levelRow}>Inventory: <BinLevel binEntity={binEntity} /></div>
                      <div className={styles.levelRow}>Open: <BinAvailableCapacity binEntity={binEntity} /></div>
                      {bin.material && (<div style={{ fontWeight: 'bold' }}>{bin.material}</div>)}
                    </div>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
}

function BinVolume({ binEntity, volume, showUnit = true }) {
  const bulkDensity = binEntity?.properties.bulkDensity || BARN_ASSUMED_BULK_DENSITY;
  const measurementOverrides = useBinLevelMeasurementOverrides({ bin: binEntity });
  const measurement = useMeasurement({ name: 'binLevel', ...measurementOverrides });
  const value = measurement.convert({ volume, bulkDensity });
  return (
    <span>
      {isNaN(value) ? '-' : Math.round(value)} {showUnit && measurement.unit}
    </span>
  );
}

function BinLevel({ binEntity }) {
  const styles = useStyles();
  const { entity: levelEntity } = useLatestLevelFromPageNow({ binHref: binEntity?.getLinkByRel('self').href });
  const latestLevel = levelEntity?.properties?.volume;
  return (
    <div className={styles.level}>
      <BinVolume binEntity={binEntity} volume={latestLevel} />
    </div>
  );
}

function BinAvailableCapacity({ binEntity }) {
  const fullTotal = binEntity?.properties.volumes.fullTotal;
  const { entity: levelEntity } = useLatestLevelFromPageNow({ binHref: binEntity?.getLinkByRel('self').href });
  const latestLevel = levelEntity?.properties?.volume;
  if (fullTotal === undefined || latestLevel === undefined) {
    return <Typography component='span'>-</Typography>;
  }
  const capacity = Math.max(0, fullTotal - latestLevel);
  return <BinVolume binEntity={binEntity} volume={capacity} />;
}
